// One-wallet-per-browser guard (v0.11 #2). Remembers, in this browser, that an
// M wallet exists — so the welcome screen leads with (only) "Log in". Persisted
// in BOTH localStorage and IndexedDB so clearing one alone doesn't reopen the
// create path; both survive everything a normal user does and only vanish if
// the site's data is deliberately cleared. Every access is guarded — private
// mode / disabled storage must never break the app.

/**
 * v1.3.0 — the welcome money. createView writes { a: address, at, amt? } here
 * when a new wallet has used its invite (the Worker funds it at once; `amt` is
 * the amount Worker 1.3.2 says it is sending — v1.3.1); the home reads it to say
 * "Welcome. Sending $10 to your wallet now to try M®." once, and removes it. Device-only: never synced, and not one of
 * walletSwitch's WALLET_KEYS (it names its wallet itself).
 */
export const WELCOME_KEY = "m.welcome";

const KEY_HAS = "m.hasWallet";
const KEY_NAME = "m.name";
const DB = "m-device";
const STORE = "kv";

export interface DeviceFlag {
  has: boolean;
  name: string | null;
}

// --- localStorage (synchronous) --------------------------------------------
export function readFlagSync(): DeviceFlag {
  try {
    return {
      has: localStorage.getItem(KEY_HAS) === "1",
      name: localStorage.getItem(KEY_NAME),
    };
  } catch {
    return { has: false, name: null };
  }
}

function lsSet(name: string | null) {
  try {
    localStorage.setItem(KEY_HAS, "1");
    if (name) localStorage.setItem(KEY_NAME, name);
  } catch {
    /* ignore */
  }
}
function lsClear() {
  try {
    localStorage.removeItem(KEY_HAS);
    localStorage.removeItem(KEY_NAME);
  } catch {
    /* ignore */
  }
}

// --- IndexedDB (async backup) ----------------------------------------------
function withStore<T>(
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(DB, 1);
    } catch (e) {
      reject(e);
      return;
    }
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => {
      const db = req.result;
      const tx = db.transaction(STORE, mode);
      const r = fn(tx.objectStore(STORE));
      r.onsuccess = () => resolve(r.result as T);
      r.onerror = () => reject(r.error);
      tx.oncomplete = () => db.close();
    };
  });
}
async function idbGet(k: string): Promise<string | null> {
  try {
    const v = await withStore<string | undefined>("readonly", (s) => s.get(k));
    return v ?? null;
  } catch {
    return null;
  }
}
async function idbSet(k: string, v: string): Promise<void> {
  try {
    await withStore("readwrite", (s) => s.put(v, k));
  } catch {
    /* ignore */
  }
}
async function idbDel(k: string): Promise<void> {
  try {
    await withStore("readwrite", (s) => s.delete(k));
  } catch {
    /* ignore */
  }
}

export async function readFlagIdb(): Promise<DeviceFlag> {
  const has = (await idbGet(KEY_HAS)) === "1";
  const name = await idbGet(KEY_NAME);
  return { has, name };
}

// --- public API ------------------------------------------------------------
export function setFlag(name: string | null): void {
  lsSet(name);
  void idbSet(KEY_HAS, "1");
  if (name) void idbSet(KEY_NAME, name);
}

export function clearFlag(): void {
  lsClear();
  void idbDel(KEY_HAS);
  void idbDel(KEY_NAME);
}
